import { Box, Flex, FormControl, Input, Stack, Switch, Tag } from "@chakra-ui/react"
import { ErrorMessage, Formik } from "formik"
import { ComponentProps, FC, useState } from "react"
import ReactSelect from "react-select"
import { SelectOption } from "src/components/shared/ReactSelect"
import { Taluka } from "src/domain/entities/taluka"
import { IVillageAddFormFields } from "."
import { fetchGujaratiSuggestions } from "../../../../utils/helpers"
import { FormikOnSubmit } from "../../../../utils/types"
import { DrawerForm, ErrorMessageField } from "../../../ui"
import { InputLabel } from "../../../ui/InputLabel"

interface Props extends Omit<ComponentProps<typeof DrawerForm>, "children"> {
	handleSubmit: FormikOnSubmit<IVillageAddFormFields>
	talukaList: Taluka[]
}

export const VillageAddDrawerFormView: FC<Props> = ({
	handleSubmit,
	talukaList,
	...rest
}) => {
	const [nameSuggestions, setNameSuggestions] = useState<string[]>([])

	return (
		<Formik<IVillageAddFormFields>
			initialValues={{
				name: {
					en: "",
					gu: "",
				},
				talukaId: "",
				sameAsParent: false,
			}}
			onSubmit={handleSubmit}
			enableReinitialize
		>
			{({ values, isSubmitting, handleChange, setFieldValue }) => {
				const handleNameChange = async (
					e: React.ChangeEvent<HTMLInputElement>,
				) => {
					setFieldValue("name.en", e.target.value)
					const suggestions = await fetchGujaratiSuggestions(e.target.value)
					setNameSuggestions(suggestions)
				}

				const talukaOptions = talukaList.map((taluka) => ({
					value: taluka.id,
					label: taluka.name.en + ` (${taluka.name.gu})`,
				}))

				return (
					<DrawerForm
						size="sm"
						headerLabel="Add Village"
						submitLabel="Save"
						isSubmitting={isSubmitting}
						{...rest}
					>
						<Stack maxWidth={"sm"} marginX={"auto"}>
							<Flex gridGap={2}>
								{/* Taluka */}
								<FormControl flex={2}>
									<InputLabel label="Taluka" />
									<ReactSelect
										name="talukaId"
										onChange={(newValue) => {
											setFieldValue(
												"talukaId",
												(newValue as SelectOption).value,
											)
										}}
										value={talukaOptions.find(
											(el) => el.value === values.talukaId,
										)}
										options={talukaOptions}
										isSearchable
									/>
								</FormControl>
							</Flex>
							<Flex gridGap={2}>
								<Box flex={5}>
									{/* Name */}
									<FormControl>
										<InputLabel label="Name" />
										<Input
											name="name.en"
											placeholder="Name"
											maxLength={50}
											required
											autoFocus
											value={values.name.en}
											onChange={handleNameChange}
										/>
										<ErrorMessage
											component={ErrorMessageField}
											name="name.en"
										/>
									</FormControl>
									<Box>
										{nameSuggestions.map((el, i) => (
											<Tag
												colorScheme={"green"}
												backgroundColor={"green.50"}
												variant="outline"
												_hover={{
													backgroundColor: "green.100",
												}}
												cursor="pointer"
												margin={0.5}
												onClick={() => {
													setFieldValue("name.gu", el)
													setNameSuggestions([])
												}}
												key={i}
											>
												{el}
											</Tag>
										))}
									</Box>
								</Box>
								{/* Name Gu */}
								<FormControl flex={5}>
									<InputLabel label="Name Gujarati" />
									<Input
										name="name.gu"
										placeholder="Name Gujarati"
										maxLength={50}
										required
										value={values.name.gu}
										onChange={handleChange}
									/>
									<ErrorMessage
										component={ErrorMessageField}
										name="name.gu"
									/>
								</FormControl>
							</Flex>
							{/* Is Same as parent */}
							<FormControl>
								<InputLabel label="Same as parent?" />
								<Box>
									<Switch
										isChecked={values.sameAsParent}
										onChange={(val) => {
											setFieldValue(
												"sameAsParent",
												val.target.checked,
											)
										}}
									/>
								</Box>
							</FormControl>
						</Stack>
					</DrawerForm>
				)
			}}
		</Formik>
	)
}
